import React from "react"
import { observer } from "mobx-react-lite"

import { UserAndGroup } from "@framework/types/user"
import { useController, useStore } from "@store"
import useDebounce from "@components/hooks/useDebounce"
import Loader from "@components/ui/Loader/BarLoader"
import NotFound from "@components/ui/NotFound/NotFound"
import { SearchContext } from "@components/prototypes/SearchContext"
import useInfiniteScroll from "@components/hooks/useInfiniteScroll"
import Skeleton from "@components/ui/Skeleton/Skeleton"
import { EntityAccessEndpoints } from "@services/entity-permissions.service"

import EntityCard from "./EntityCard"

import styles from "./ParticipantList.module.sass"

export interface EntityParticipantListProps {
  entityId: string
  loading?: boolean
  editable?: boolean
  entityType: EntityAccessEndpoints
  isSelected: (value: UserAndGroup) => boolean
  onSelect: (value: UserAndGroup) => void
}

const EntityParticipantList: React.FC<EntityParticipantListProps> = observer(
  ({
    entityId,
    loading = false,
    editable = true,
    entityType,
    onSelect,
    isSelected,
  }) => {
    const {
      entityPermissionsStore: { permittedEntitiesStore: permissionsCollection },
    } = useStore()

    const { entityPermissionsController } = useController()

    const searchContext = React.useContext(SearchContext)
    const query = searchContext?.query ?? ""

    const { debouncedValue: searchQuery, setDebounce } = useDebounce({
      value: query,
    })

    const loadMoreContent = () => {
      entityPermissionsController.loadPermittedEntities(entityId, entityType, {
        pageNum: permissionsCollection.meta.pageNum + 1,
        pageSize: 10,
        query: searchQuery,
      })
    }

    const hasMoreContent =
      (permissionsCollection.meta?.pageNum || 1) <
      (permissionsCollection.meta?.totalPages || 0)

    const lastItemRef = useInfiniteScroll({
      callback: loadMoreContent,
      isLoading: permissionsCollection.isLoading,
      hasMore: hasMoreContent,
    })

    React.useEffect(() => {
      setDebounce(query)
    }, [query])

    React.useEffect(() => {
      entityPermissionsController.loadPermittedEntities(
        entityId,
        entityType,
        {
          pageNum: 1,
          pageSize: 10,
          query: searchQuery,
        },
        true
      )
    }, [searchQuery, permissionsCollection.reloadTrigger])

    const { data: entities } = permissionsCollection

    const isLoading = permissionsCollection.isLoading || loading

    return (
      <div className={styles.root}>
        {!entities.length ? (
          isLoading ? (
            <Loader fluid size="large" />
          ) : searchQuery ? (
            <NotFound fluid>No user or group found by query</NotFound>
          ) : (
            <NotFound fluid>Nobody has access yet</NotFound>
          )
        ) : (
          <div className={styles.list}>
            {entities.map((entity) => {
              return (
                <EntityCard
                  key={entity.id}
                  user={entity}
                  toRemove
                  {...(editable
                    ? {
                        isSelected: isSelected(entity),
                        onSelect,
                      }
                    : null)}
                />
              )
            })}
            {hasMoreContent && (
              <div ref={lastItemRef}>
                <Skeleton
                  count={permissionsCollection.meta.pageSize || 3}
                  lineHeight={56}
                  minWidth={100}
                  rounded
                />
              </div>
            )}
          </div>
        )}
      </div>
    )
  }
)

export default EntityParticipantList
